import { useContext, useEffect, useRef } from "react";
import { useRouter } from "next/router";

import { AppContext } from "@vahak/core/dist/app-context";
import { API_REQUEST_TYPE } from "@vahak/core/dist/constants/api";
import { LORRY_STATUS } from "@vahak/core/dist/constants/lorryStatus";
import { toast } from "@vahak/core/dist/components/toast/toast";
import { useGetLorriesForCurrentUserQuery } from "@vahak/core/dist/_services/hooks/useLorryMarketService";
import { LorryListTypes } from "@vahak/core/dist/custom-types/lorry";

import {
    DEEP_LINK_LORRY_ID_PARAM,
    DEEP_LINK_QUERY_PARAM,
    DEEP_LINK_QUERY_PARAM_LEGACY,
    getRedirectQueryValue,
    isAuthRequiredDeepLink,
    isLoadAddDeepLink,
    isLoadDetailsDeepLink,
    isLorryReactivateDeepLink,
    isMyLoadsListingPath,
    isMyLorriesListingPath
} from "../constants/deep-links";
import { POSTING_FORM_MODAL_ORIGINATION_PAGE, useLoadLorryPosting } from "../context/load-lorry-posting";
import { useCompactLoadPostingOptional } from "../components/homepage-v2/compact-load-posting/CompactLoadPostingProvider";
import { getLorryPreFilledData } from "../components/dashboard/list-wrapper/lorry-list/lorry-card/LorryCard";
import { LORRY_CREATION_FORM_IDS } from "../components/posting/helpers/constants";
import { LORRY_POSTING_FORM_LOCATION_STATE } from "../components/posting/lorry-posting/helpers/constants";
import { generataMaskUrlWithParams } from "../methods/generataMaskUrlWithParams";
import useOpenNewLoadPosting from "./useOpenNewLoadPosting";
import useValidateUserStatus from "./useValidateUserStatus";

function isHomepagePath(pathname: string): boolean {
    return pathname === "/";
}

function isDashboardPath(pathname: string, asPath: string): boolean {
    const pathWithoutQuery = asPath.split("?")[0].split("#")[0];
    return pathname.endsWith("/dashboard") || pathWithoutQuery === "/dashboard";
}

function stripDeepLinkQuery(router: ReturnType<typeof useRouter>, extraKeys: string[] = []) {
    const newQuery = { ...router.query };
    delete newQuery[DEEP_LINK_QUERY_PARAM];
    delete newQuery[DEEP_LINK_QUERY_PARAM_LEGACY];
    extraKeys.forEach((key) => {
        delete newQuery[key];
    });
    delete newQuery._branch_match_id;
    delete newQuery._branch_referrer;
    delete newQuery.utm_medium;
    delete newQuery.utm_source;
    delete newQuery.utm_campaign;
    delete newQuery.u_i;
    router.replace({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true });
}

function openLoginModal(router: ReturnType<typeof useRouter>) {
    if (router.asPath.split("#")[1]?.includes("Login")) {
        return;
    }

    // Hash only — passing `query` rewrites `action=load|details` and retriggers this effect.
    router.push({ hash: "Login" }, undefined, { shallow: true });
}

/**
 * Handles web `?action=` deep links mounted app-wide from CompactLoadPostingProvider.
 *
 * - `load|add` — compact posting (logged-out on `/`, logged-in on `/dashboard`)
 * - `load|details` — my-loads listing `?id=` (auth required; HomepageV2AuthGuard / post-login redirect)
 * - `lorry|reactivate` — my-lorries listing, then lorry re-post modal (auth required)
 */
export function useLoadAddDeepLink() {
    const router = useRouter();
    const { isPhoneVerified, companyId, customerCompanyId, setShouldPreventLayoutSwitch } = useContext(AppContext);
    const openCompactPosting = useCompactLoadPostingOptional()?.openCompactPosting;
    const openNewLoadPosting = useOpenNewLoadPosting();
    const { updateLoadLorryPostingData } = useLoadLorryPosting();
    const { ValidateLorryPosting } = useValidateUserStatus();
    const hasHandledRef = useRef(false);
    const hasOpenedLoginRef = useRef(false);

    const isLoggedIn = Boolean(isPhoneVerified && companyId);
    const isLoadAdd = isLoadAddDeepLink(router.query, router.asPath);
    const isLoadDetails = isLoadDetailsDeepLink(router.query, router.asPath);
    const isLorryReactivate = isLorryReactivateDeepLink(router.query, router.asPath);
    const needsAuth = isAuthRequiredDeepLink(router.query, router.asPath);

    const lorryId = isLorryReactivate ? getRedirectQueryValue(router.query, DEEP_LINK_LORRY_ID_PARAM) : undefined;
    const listingCompanyId = Number(customerCompanyId || companyId) || undefined;
    const onMyLorriesListing = isMyLorriesListingPath(router.pathname, router.asPath);

    const {
        data: lorryQueryData,
        isFetching: isFetchingLorry,
        isLoading: isLoadingLorry
    } = useGetLorriesForCurrentUserQuery(
        {
            companyId: listingCompanyId,
            request_type: API_REQUEST_TYPE.ELASTIC_SEARCH,
            id: lorryId ? [Number(lorryId)] : undefined
        },
        {
            enabled: Boolean(isLoggedIn && isLorryReactivate && lorryId && onMyLorriesListing && listingCompanyId)
        }
    );

    useEffect(() => {
        if (!router.isReady || hasHandledRef.current) {
            return;
        }

        if (!isLoadAdd && !isLoadDetails && !isLorryReactivate) {
            return;
        }

        const onHomepage = isHomepagePath(router.pathname);
        const onDashboard = isDashboardPath(router.pathname, router.asPath);
        const onMyLoadsListing = isMyLoadsListingPath(router.pathname, router.asPath);

        if (needsAuth && !isLoggedIn) {
            if (!onHomepage || hasOpenedLoginRef.current) {
                return;
            }

            hasOpenedLoginRef.current = true;
            setShouldPreventLayoutSwitch?.(true);
            openLoginModal(router);
            return;
        }

        if (isLoadAdd) {
            if (isLoggedIn) {
                if (!onDashboard) {
                    return;
                }

                hasHandledRef.current = true;
                openNewLoadPosting({
                    originationPage: POSTING_FORM_MODAL_ORIGINATION_PAGE.DASHBOARD,
                    afterOpen: () => {
                        stripDeepLinkQuery(router);
                    }
                });
                return;
            }

            if (!onHomepage || !openCompactPosting) {
                return;
            }

            openCompactPosting();
            hasHandledRef.current = true;
            stripDeepLinkQuery(router);
            return;
        }

        if (isLoadDetails) {
            if (!onMyLoadsListing) {
                return;
            }

            hasHandledRef.current = true;
            stripDeepLinkQuery(router);
            return;
        }

        if (!onMyLorriesListing) {
            return;
        }

        if (!lorryId) {
            hasHandledRef.current = true;
            toast.error("Lorry id is missing");
            stripDeepLinkQuery(router, [DEEP_LINK_LORRY_ID_PARAM]);
            return;
        }

        if (!listingCompanyId || isFetchingLorry || isLoadingLorry) {
            return;
        }

        const lorryCard = lorryQueryData?.pages?.[0]?.results?.[0] as LorryListTypes | undefined;
        hasHandledRef.current = true;

        if (!lorryCard?.lorry_detail) {
            toast.error("Lorry not found");
            stripDeepLinkQuery(router, [DEEP_LINK_LORRY_ID_PARAM]);
            return;
        }

        if (lorryCard.lorry_detail.status !== LORRY_STATUS.IN_ACTIVE) {
            toast.error("This lorry cannot be reactivated");
            stripDeepLinkQuery(router, [DEEP_LINK_LORRY_ID_PARAM]);
            return;
        }

        const lorryDetailId = lorryCard.lorry_detail.id;
        ValidateLorryPosting?.(() => {
            updateLoadLorryPostingData({
                category: "re-post",
                isModalOpen: true,
                type: "lorry",
                data: {
                    id: lorryDetailId,
                    values: getLorryPreFilledData(lorryCard)
                },
                originationPage: POSTING_FORM_MODAL_ORIGINATION_PAGE.DASHBOARD
            });

            const newQuery = { ...router.query };
            delete newQuery[DEEP_LINK_QUERY_PARAM];
            delete newQuery[DEEP_LINK_QUERY_PARAM_LEGACY];
            delete newQuery[DEEP_LINK_LORRY_ID_PARAM];
            delete newQuery._branch_match_id;
            delete newQuery._branch_referrer;
            delete newQuery.utm_medium;
            delete newQuery.utm_source;
            delete newQuery.utm_campaign;
            delete newQuery.u_i;
            newQuery[LORRY_POSTING_FORM_LOCATION_STATE.PARAM_NAME] = LORRY_CREATION_FORM_IDS.VEHICLE_ROUTES_SELECTION;

            router.push({ query: newQuery }, generataMaskUrlWithParams(newQuery), {
                shallow: true
            });
        });
    }, [
        router.isReady,
        router.pathname,
        router.asPath,
        isLoadAdd,
        isLoadDetails,
        isLorryReactivate,
        needsAuth,
        isLoggedIn,
        openCompactPosting,
        openNewLoadPosting,
        onMyLorriesListing,
        lorryId,
        listingCompanyId,
        isFetchingLorry,
        isLoadingLorry,
        lorryQueryData,
        ValidateLorryPosting,
        updateLoadLorryPostingData,
        setShouldPreventLayoutSwitch
    ]);
}
